import { useEffect } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import FloatingChatButton from "@/components/FloatingChatButton";
import WhatsAppWidget from "@/components/WhatsAppWidget";
import SeoHead from "@/components/seo/SeoHead";
import { SeoHero, SeoSection, InfoCards, FaqList, LinkChips, CtaBand } from "@/components/seo/SeoBlocks";
import { useLanguage } from "@/contexts/LanguageContext";
import { CITY_SERVICES } from "@/seo/seoConfig";
import { LABELS } from "@/seo/seoLabels";
import { cityPages } from "@/seo/cityPages";
import { servicePages } from "@/seo/servicePages";

const CityPage = ({ page }) => {
  const { language: lang } = useLanguage();
  const L = LABELS[lang];
  useEffect(() => { window.scrollTo(0, 0); }, [page.slug]);

  const featured = servicePages.filter((s) => page.featuredServices.includes(s.slug));
  const nearby = cityPages.filter((c) => page.nearby.includes(c.slug));

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`city-page-${page.slug}`}>
      <SeoHead title={page.title} description={page.metaDescription} path={`/${page.slug}`} />
      <Navigation />
      <SeoHero
        crumbs={[{ label: L.home, to: "/" }, { label: L.serviceAreas, to: "/#areas" }, { label: `${page.city}, VA` }]}
        eyebrow={L.cityEyebrow}
        h1={page.h1[lang]}
        intro={page.intro[lang][0]}
      />

      <SeoSection testId="city-intro">
        <p className="text-slate-600 text-sm md:text-base leading-relaxed max-w-4xl mb-8">{page.intro[lang][1]}</p>
        <h2 className="text-xl md:text-2xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{L.neighborhoods(page.city)}</h2>
        <LinkChips items={page.neighborhoods.map((n) => ({ label: n }))} testId="neighborhood-chips" />
      </SeoSection>

      <SeoSection alt title={L.servicesIn(page.city)} testId="city-services">
        <p className="text-slate-600 text-sm md:text-base mb-6 max-w-4xl">{L.servicesInText(page.city)}</p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {CITY_SERVICES.map((s, i) => (
            <li key={i} className="flex items-start gap-2 bg-white border border-slate-200 p-3 md:p-4">
              <CheckCircle2 className="w-4 h-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              {s.slug ? (
                <Link to={`/${s.slug}`} className="text-sm md:text-base text-[#0F172A] hover:text-[#B5952F] font-medium transition-colors" data-testid={`city-service-link-${s.slug}`}>{s[lang]}</Link>
              ) : (
                <span className="text-sm md:text-base text-slate-700">{s[lang]}</span>
              )}
            </li>
          ))}
        </ul>
      </SeoSection>

      <SeoSection title={page.local.heading[lang]} testId="city-local">
        <InfoCards items={page.local.items[lang]} />
      </SeoSection>

      <SeoSection alt title={L.faq} testId="city-faq">
        <FaqList items={page.faq[lang]} />
      </SeoSection>

      <SeoSection testId="city-links">
        <h2 className="text-xl md:text-2xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{L.popularServices(page.city)}</h2>
        <div className="mb-8"><LinkChips items={featured.map((s) => ({ label: s.name[lang], to: `/${s.slug}` }))} testId="featured-service-links" /></div>
        <h2 className="text-xl md:text-2xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>{L.nearbyAreas}</h2>
        <LinkChips items={nearby.map((c) => ({ label: `${c.city}, VA`, to: `/${c.slug}` }))} testId="nearby-city-links" />
      </SeoSection>

      <CtaBand title={L.ctaBandCity(page.city)} text={L.ctaBandText} />
      <Footer />
      <FloatingChatButton />
      <WhatsAppWidget />
    </div>
  );
};

export default CityPage;
